import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommentModerationData, dashboardApi } from '@/lib/services/dashboard-api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle, ArrowRight, Clock, Check, X, AlertTriangle, Heart, MessageSquare } from 'lucide-react';
import { formatDistanceToNowSafe } from '@/lib/utils/date';

interface PendingCommentsListProps {
  onViewMore?: () => void;
  maxComments?: number;
}

export function PendingCommentsList({ onViewMore, maxComments = 5 }: PendingCommentsListProps) {
  const navigate = useNavigate();
  const [comments, setComments] = useState<CommentModerationData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingComments();
  }, []);

  const fetchPendingComments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await dashboardApi.getPendingComments(maxComments);
      setComments(data);
    } catch (err) {
      console.error('Error fetching pending comments:', err);
      setError('Failed to load pending comments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModerateComment = async (commentId: number, action: 'approve' | 'reject') => {
    try {
      const success = await dashboardApi.moderateComment(commentId, action);
      if (success) {
        // Remove the comment from the list or refresh
        setComments(prev => prev.filter(comment => comment.id !== commentId));
      }
    } catch (error) {
      console.error('Error moderating comment:', error);
      toast.error('Failed to moderate comment');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Pending Comments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(maxComments)].map((_, i) => (
              <div key={i} className="flex items-start space-x-3 animate-pulse p-3 rounded-lg border">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Pending Comments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPendingComments}
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Pending Comments
          {comments.length > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {comments.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {comments.length === 0 ? (
          <div className="text-center py-6">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No pending comments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => (
              <div
                key={comment.id}
                onClick={() => navigate(`/streams/${comment.stream?.id}`)}
                className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left w-full cursor-pointer"
              >
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarImage src={comment.author.avatar} alt={comment.author.name} />
                  <AvatarFallback className="text-xs">
                    {getInitials(comment.author.name)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{comment.author.name}</p>
                    <Badge
                      variant="secondary"
                      className={`text-xs px-1.5 py-0.5 ${getStatusColor(comment.status)}`}
                    >
                      {comment.status}
                    </Badge>
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {truncateText(comment.content)}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                    {comment.stream && (
                      <div className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        <span className="truncate">{comment.stream.title}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      <Heart className="h-3 w-3" />
                      <span>{comment.likes}</span>
                    </div>

                    {comment.reports > 0 && (
                      <div className="flex items-center gap-1 text-orange-600">
                        <AlertTriangle className="h-3 w-3" />
                        <span>{comment.reports} reports</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNowSafe(comment.createdAt)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModerateComment(comment.id, 'approve');
                      }}
                      className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Approve
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModerateComment(comment.id, 'reject');
                      }}
                      className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {onViewMore && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewMore}
                  className="w-full justify-between"
                >
                  Moderate All Comments
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
